import { useState } from 'react';

import {
  Box,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@mui/material';

import { useAppSelector } from 'state/hooks';
import { selectMapStyle } from 'state/workflowSlice';

import { MapStyle } from 'appConstants';
import { ColorScheme, LegendSettings, LegendType } from 'appTypes';
import { StlPopup } from 'features/common/StlPopup/StlPopup';
import {
  IMetricLegendConfig,
  LegendColorSchemes,
  LegendStops,
} from 'features/workflow_timeline/metricsOptions';

import { classNames } from '../common/utils';
import { LegendSlider } from './LegendSlider';
import { MapMetricLegend } from './MapMetricLegend';

import styles from './LegendSettingsModal.module.css';

export function PreviewSlider({
  colormap,
  description,
  selected = false,
  stops,
  legendType,
}: {
  colormap: ColorScheme;
  description?: string;
  selected?: boolean;
  stops: LegendStops;
  legendType: LegendType;
}) {
  return (
    <>
      {description && (
        <span
          className={classNames(
            styles.preview_description,
            selected && styles.preview_description_selected,
          )}
        >
          {description}
        </span>
      )}
      <LegendSlider
        colormap={colormap}
        stops={stops}
        legendType={legendType}
        className={styles.preview_slider}
      />
    </>
  );
}

type ColorSchemeOption = {
  value: ColorScheme;
  colormap: (number | string)[];
};

type LegendTypeOption = {
  value: LegendType;
  label: string;
};

const LEGEND_TYPES: LegendTypeOption[] = [
  { value: 'discrete', label: 'Discrete' },
  { value: 'continuous', label: 'Continuous' },
];

export const COLOR_SCHEME_DESCRIPTIONS: Record<ColorScheme, string> = {
  neutral: 'Red, Yellow, Neutral',
  green: 'Red, Yellow, Green',
};

export const COLOR_SCHEME_DESCRIPTIONS_DIVERGING: Record<ColorScheme, string> =
  {
    neutral: 'Red, Yellow, Neutral, Blue',
    green: 'Red, Yellow, Green, Blue',
  };

interface LegendSettingsProps {
  type: LegendType;
  setType: (val: LegendType) => void;
  colorScheme: ColorScheme;
  colorSchemes: LegendColorSchemes;
  setColorScheme: (val: ColorScheme) => void;
  stops: LegendStops;
  hideTypeSelector?: boolean;
  isMetricDiverging: boolean;
}

export function LegendSettingsForm({
  type,
  setType,
  colorScheme,
  colorSchemes,
  setColorScheme,
  stops,
  hideTypeSelector = false,
  isMetricDiverging,
}: LegendSettingsProps) {
  // Rendered value uses the selected item from the array
  const selectedTypeMap = colorSchemes[type];
  const selectedColorMap = selectedTypeMap[colorScheme];
  const colorSchemeDescriptions = isMetricDiverging
    ? COLOR_SCHEME_DESCRIPTIONS_DIVERGING
    : COLOR_SCHEME_DESCRIPTIONS;
  return (
    <FormControl fullWidth className={styles.form_control}>
      {/* Type Selector */}
      {!hideTypeSelector && (
        <div className={styles.form_row}>
          <Typography variant="body1">Type</Typography>
          <RadioGroup
            row
            name="legendType"
            value={type}
            onChange={(event) => setType(event.target.value as LegendType)}
            className={styles.radio_group}
          >
            {LEGEND_TYPES.map((opt) => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                className={styles.form_control_label}
                control={<Radio />}
                label={opt.label}
              />
            ))}
          </RadioGroup>
        </div>
      )}
      {/* Color Scheme Selector */}
      <div className={styles.form_row}>
        <Typography variant="body1">Legend Colors</Typography>
        <Select
          value={colorScheme}
          onChange={(e) =>
            setColorScheme(e.target.value as ColorSchemeOption['value'])
          }
          className={styles.select_root}
          renderValue={() => (
            <PreviewSlider
              colormap={selectedColorMap}
              legendType={type}
              stops={stops}
            />
          )}
          MenuProps={{
            PaperProps: {
              className: styles.menu_paper,
            },
          }}
        >
          {['green', 'neutral']
            .filter((v) => v in colorSchemes[type]) // Forces ordering
            .map((key) => {
              const colormap = colorSchemes[type][key];
              return (
                <MenuItem key={key} value={key} className={styles.menu_item}>
                  <Box className={styles.menuBox}>
                    <PreviewSlider
                      colormap={colormap}
                      stops={stops}
                      legendType={type}
                      description={
                        colorSchemeDescriptions[key as 'neutral' | 'green']
                      }
                      selected={key === colorScheme}
                    />
                  </Box>
                </MenuItem>
              );
            })}
        </Select>
      </div>
    </FormControl>
  );
}

export function LegendSettingsModal({
  metricSettings,
  setShowSettings,
  showSettings,
  title,
  hideTypeSelector = false,
  updateLegendSettings,
  initLegendSettings,
  reversedDisplay = false,
}: {
  metricSettings: IMetricLegendConfig;
  title?: string;
  showSettings: boolean;
  setShowSettings: (val: boolean) => void;
  hideTypeSelector?: boolean;
  updateLegendSettings: (val: LegendSettings) => void;
  initLegendSettings: LegendSettings;
  reversedDisplay?: boolean;
}) {
  const [legend, setLegend] = useState<LegendSettings>(initLegendSettings);
  const mapStyle = useAppSelector(selectMapStyle);

  const closeModal = () => {
    setShowSettings(false);
  };

  const legendType = metricSettings.color_schemes[legend.type];
  const colors = legendType[legend.scheme];

  const handleApply = () => {
    updateLegendSettings(legend);
    setShowSettings(false);
  };

  return (
    <StlPopup
      title="Map Legend Customization"
      btnTxt="Save"
      isOpen={showSettings}
      onCancel={closeModal}
      onClose={closeModal}
      onApply={handleApply}
      sx={{ minWidth: '49rem' }}
    >
      <div className={styles.legend_customization}>
        <div
          className={classNames(
            styles.legend_box,
            styles.legend_preview,
            mapStyle === MapStyle.Sat && styles.legend_preview_sat,
            mapStyle === MapStyle.Light && styles.legend_preview_light,
          )}
        >
          <span
            className={classNames(
              styles.legend_preview_title,
              mapStyle === MapStyle.Light && styles.legend_preview_title_dark,
            )}
          >
            Preview
          </span>
          <MapMetricLegend
            colors={colors}
            legendType={legend.type}
            title={title}
            stops={metricSettings.legend_stops}
            displayVolumeThickness={
              metricSettings.display_volume_thickness ?? true
            }
            preview
            reversedDisplay={reversedDisplay}
          />
        </div>
        <div className={classNames(styles.legend_box, styles.legend_settings)}>
          <LegendSettingsForm
            type={legend.type}
            setType={(val: LegendType) =>
              setLegend((prev) => ({ ...prev, type: val }))
            }
            colorSchemes={metricSettings.color_schemes}
            colorScheme={legend.scheme}
            setColorScheme={(val: ColorScheme) =>
              setLegend((prev) => ({ ...prev, scheme: val }))
            }
            stops={metricSettings.legend_stops}
            hideTypeSelector={hideTypeSelector}
            isMetricDiverging={metricSettings.is_metric_diverging}
          />
        </div>
      </div>
    </StlPopup>
  );
}
